import { ensureServiceWorkerAwake, getLastServiceWorkerPong } from 'shared/channels/ensureServiceWorkerAwake';
import { frontendVersionWithBuildNumber } from 'shared/version';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { getServiceWorkerRegistrationState } from './getServiceWorkerRegistrationState';
import { readDatabaseSyncSnapshot } from './readDatabaseSyncSnapshot';
import { requestServiceWorkerHealthReport } from './requestServiceWorkerHealthReport';
import type { DiagnosticsStepFailure, ServiceWorkerDiagnostics } from './types';

const DIAGNOSTICS_STEP_TIMEOUT_MS = 2_000;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`timed out after ${timeoutMs} ms`)), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function settleStep<T>(step: () => Promise<T>): Promise<T | DiagnosticsStepFailure> {
  try {
    return await withTimeout(step(), DIAGNOSTICS_STEP_TIMEOUT_MS);
  } catch (error) {
    return { failed: getErrorMessage(error) || 'unknown error' };
  }
}

/**
 * Snapshot explaining a missing service worker response: registration state,
 * the worker's own health report and the sync state of every IndexedDB entity
 * (read by the page, so it is there even when the worker is down). Never rejects.
 */
export async function collectServiceWorkerDiagnostics(): Promise<ServiceWorkerDiagnostics> {
  const [wakeResult, registration, health, database] = await Promise.all([
    settleStep(() => ensureServiceWorkerAwake()),
    settleStep(getServiceWorkerRegistrationState),
    settleStep(() => requestServiceWorkerHealthReport(DIAGNOSTICS_STEP_TIMEOUT_MS)),
    settleStep(readDatabaseSyncSnapshot),
  ]);
  return {
    pageVersion: frontendVersionWithBuildNumber,
    wakeResult,
    registration,
    health,
    // After the wake-up above: it is the pong that answered it.
    pong: getLastServiceWorkerPong(),
    database,
  };
}
